import React, { useMemo } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../../../common/types';
import { useBlocks, type BlockRow } from '../hooks/useBlocks';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { campaignPageMessages as messages } from './campaignPageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the campaign page; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Campaign.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The chips that the side card shows; the rest is a count. */
const SHOWN = 8;

const KIND_LABEL: Record<BlockRow['kind'], MessageDescriptor> = {
  email: messages.blockKindEmail,
  domain: messages.blockKindDomain,
  playlist: messages.blockKindPlaylist,
  contact: messages.blockKindContact,
};

/**
 * "Never contact" (design outreach-v2 g1 side): the artist's block list (`outreach.blocks.*`). The
 * cross on a chip removes it; a block of a row adds to it.
 */
const NeverContactSideComponent: React.FC<{ accountId: number }> = ({ accountId }) => {
  const intl = useIntl();
  const { isDemo } = useOutreachPlan();
  const { blocks, removeBlock } = useBlocks(accountId);
  const chips = useMemo(
    () =>
      blocks.slice(0, SHOWN).map((block) => (
        <span key={block.blockUuid} className={styles.ncChip} data-testid="never-chip">
          <span className={styles.ncKind}>{intl.formatMessage(KIND_LABEL[block.kind])}</span>
          {block.label}
          {!isDemo && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              aria-label={intl.formatMessage(messages.neverRemove, { label: block.label })}
              onClick={() => removeBlock(block.blockUuid).catch(console.error)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </span>
      )),
    [blocks, intl, isDemo, removeBlock]
  );
  return (
    <section className={styles.card} data-testid="never-contact-side">
      <div className={styles.cardHead}>
        <h3 className={styles.cardTitle}>{intl.formatMessage(messages.neverTitle)}</h3>
        <span className={styles.meta}>{intl.formatNumber(blocks.length)}</span>
      </div>
      {blocks.length === 0 ? (
        <p className={`${shared.small} m-0`}>{intl.formatMessage(messages.neverNone)}</p>
      ) : (
        <div className={styles.chips}>
          {chips}
          {blocks.length > SHOWN && (
            <span className={shared.small}>
              {intl.formatMessage(messages.neverMore, { count: blocks.length - SHOWN })}
            </span>
          )}
        </div>
      )}
    </section>
  );
};

export const NeverContactSide = TypedMemo(NeverContactSideComponent);
